import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { api } from '../../../services/api';
import MailSheet from './MailSheet';
import GmailConnectPanel from './GmailConnectPanel';
import { C, S, btn, clampLines } from './mailStyles';
import { absoluteTime, mailErrorKind, monogram, monogramColor, parseSender } from './mailUtils';

const LONG_BODY_CHARS = 600;
const LONG_BODY_LINES = 10;

function Skeleton({ width = '100%', height = 12 }) {
  return <div style={{ width, height, borderRadius: 6, background: C.bgMuted }} />;
}

export default function MailDetailSheet({ item, onClose, onDraftReply, onStatusChanged }) {
  const messageId = item?.message_id;
  const [reloadKey, setReloadKey] = useState(0);
  const [load, setLoad] = useState({ state: 'loading', detail: null });
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [updateError, setUpdateError] = useState(null);

  useEffect(() => {
    if (!messageId) return undefined;
    const controller = new AbortController();
    api.getMailItem(messageId, { signal: controller.signal })
      .then((detail) => {
        if (!controller.signal.aborted) setLoad({ state: 'ok', detail: detail || {} });
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        const kind = mailErrorKind(err);
        setLoad({ state: kind === 'auth' || kind === 'disabled' ? kind : 'error', detail: null });
      });
    return () => controller.abort();
  }, [messageId, reloadKey]);

  const retry = () => {
    setLoad({ state: 'loading', detail: null });
    setReloadKey((k) => k + 1);
  };

  const detail = load.detail || {};
  const from = detail.from || item?.from;
  const sender = parseSender(from);
  const subject = detail.subject || item?.subject || '(no subject)';
  const summary = detail.summary || item?.summary;
  const body = typeof detail.body === 'string' ? detail.body : (typeof detail.snippet === 'string' ? detail.snippet : '');
  const isLong = body.length > LONG_BODY_CHARS || body.split('\n').length > LONG_BODY_LINES;

  const setStatus = async (status) => {
    if (updating || !messageId) return;
    setUpdating(status);
    setUpdateError(null);
    try {
      await api.updateMailItems([messageId], status);
      onStatusChanged?.(messageId, status);
      onClose?.();
    } catch {
      setUpdateError("Couldn't update this email. Try again.");
      setUpdating(null);
    }
  };

  const footer = (
    <>
      {updateError && <p style={S.error}>{updateError}</p>}
      <button
        type="button"
        style={btn({ ...S.btnPrimary, flex: 'none', width: '100%' }, Boolean(updating))}
        disabled={Boolean(updating)}
        onClick={() => onDraftReply?.(item, sender.name)}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Sparkles size={14} aria-hidden="true" />
          Draft a reply
        </span>
      </button>
      <div style={S.actions}>
        <button type="button" style={btn(S.btnSecondary, Boolean(updating))} disabled={Boolean(updating)} onClick={() => setStatus('done')}>
          {updating === 'done' ? 'Marking…' : 'Mark done'}
        </button>
        <button type="button" style={btn(S.btnSecondary, Boolean(updating))} disabled={Boolean(updating)} onClick={() => setStatus('dismissed')}>
          {updating === 'dismissed' ? 'Dismissing…' : 'Dismiss'}
        </button>
      </div>
    </>
  );

  return (
    <MailSheet title="Email" onClose={onClose} footer={footer} testId="mail-detail-sheet">
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 18, background: monogramColor(sender.name), color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0,
        }}>
          {monogram(sender.name)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ ...S.ellipsis, fontSize: 14, fontWeight: 700 }}>{sender.name}</div>
          <div style={{ ...S.ellipsis, fontSize: 12, color: C.textDim }}>
            {[sender.address, absoluteTime(detail.received_at || item?.received_at)].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>
      <p style={{ margin: 0, fontSize: 17, fontWeight: 700, lineHeight: 1.35, ...clampLines(3) }}>{subject}</p>

      {summary && (
        <div style={{ background: C.bgMuted, borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ ...S.label, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Sparkles size={11} aria-hidden="true" />
            AI summary
          </span>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.45, overflowWrap: 'anywhere' }}>{summary}</p>
        </div>
      )}

      {load.state === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="mail-detail-loading">
          <Skeleton width="90%" />
          <Skeleton width="100%" />
          <Skeleton width="75%" />
          <Skeleton width="60%" />
        </div>
      )}

      {load.state === 'auth' && (
        <GmailConnectPanel disconnected disconnectedSub="Reconnect to see this email again." bare onConnected={retry} />
      )}

      {load.state === 'disabled' && (
        <p style={{ ...S.sub, color: C.textFaint }} data-testid="mail-unavailable">Not available</p>
      )}

      {load.state === 'error' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          <p style={S.error}>Couldn't load this email.</p>
          <button type="button" style={S.btnLink} onClick={retry}>Retry</button>
        </div>
      )}

      {load.state === 'ok' && (
        body ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div
              data-testid="mail-detail-body"
              style={{
                fontSize: 13.5, lineHeight: 1.5, color: C.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                ...(expanded
                  ? { maxHeight: '45vh', overflowY: 'auto' }
                  : isLong ? { maxHeight: '13.5em', overflow: 'hidden' } : {}),
              }}
            >
              {body}
            </div>
            {isLong && (
              <button type="button" style={{ ...S.btnLink, alignSelf: 'flex-start' }} onClick={() => setExpanded((v) => !v)}>
                {expanded ? 'Show less' : 'Show full email'}
              </button>
            )}
          </div>
        ) : (
          <p style={S.sub}>This email has no text content.</p>
        )
      )}
    </MailSheet>
  );
}
